/** @shared-compat: Client Portal and Worker share this transitional entitlement contract. */
/** Features the Client Portal can expose. The database may narrow this list. */
export const CLIENT_PORTAL_FEATURE_KEYS = [
  'tasks',
  'websites',
  'forms',
  'docs',
  'billing',
  'messages',
  'automations',
  'alice_chat',
  'alice_files',
  'alice_voice',
  'search',
  'voice_agents',
  'calls',
] as const;

export type ClientPortalFeatureKey = typeof CLIENT_PORTAL_FEATURE_KEYS[number];

export type ClientPortalCapabilityLimit = number | string | boolean;

export interface ClientPortalCapability {
  enabled: boolean;
  limits: Record<string, ClientPortalCapabilityLimit>;
}

export interface ClientPortalCapabilities {
  planKey: string | null;
  planName: string | null;
  resolvedAt: string;
  features: Record<ClientPortalFeatureKey, ClientPortalCapability>;
}

export type ClientPortalUsageUnit =
  | 'request'
  | 'launch'
  | 'search'
  | 'message'
  | 'file'
  | 'byte'
  | 'milestone';

export interface ClientPortalUsageRollup {
  featureKey: ClientPortalFeatureKey;
  unit: string;
  quantity: number;
}

export interface ClientPortalUsageResponse {
  clientId: string;
  window: { start: string; end: string };
  portalEvents: ClientPortalUsageRollup[];
  alice: {
    requests: number;
  };
  voice: {
    calls: number;
    minutes: number;
  };
  generatedAt: string;
}

export function createClientPortalCapabilityMap(
  enabled: boolean,
): Record<ClientPortalFeatureKey, ClientPortalCapability> {
  return Object.fromEntries(
    CLIENT_PORTAL_FEATURE_KEYS.map((key) => [key, { enabled, limits: {} }]),
  ) as Record<ClientPortalFeatureKey, ClientPortalCapability>;
}

export function hasClientPortalCapability(
  capabilities: ClientPortalCapabilities | null | undefined,
  featureKey: ClientPortalFeatureKey,
): boolean {
  return capabilities?.features[featureKey]?.enabled === true;
}
